import type { ReactNode } from "react";
import { Clock, ListChecks, Scale, TriangleAlert } from "lucide-react";
import type { TaskSummary } from "@patent-studio/api-types";
import { Menu, MenuItem, MenuSeparator } from "@/components/ui/Menu";
import { StatusPill } from "@/components/ui/StatusPill";
import { dayOf, dayOfDate } from "@/lib/day";
import { LiftingItem } from "./ClientCard";
import { resolveDraftTitle } from "../layout/draft-title";
import { MaybeLink } from "../layout/MaybeLink";
import { GoesTo } from "../layout/wayfinding";
import { JURISDICTION_LABEL } from "./DraftDetailsFields";
import { draftOpeningPath, findStage } from "../stage-vocabulary";
import { isCompleted, isGenerating, needsAttention } from "./draft-standing";

function Mark({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-[5px]">
      {icon}
      {children}
    </span>
  );
}

const ICON = "size-[13px] text-subtle-foreground";

/**
 * The line under the names: a failed run first, then the office, then the
 * deadline and where it opens, or the day it was completed.
 */
function Marks({ draft, done }: { draft: TaskSummary; done: boolean }) {
  const stage = findStage(draft.last_stage ?? "intake");
  return (
    <div className="mt-1.5 flex flex-wrap gap-x-3.5 gap-y-1.5 text-[12px] text-muted-foreground">
      {needsAttention(draft) && (
        <StatusPill
          tone="destructive"
          size="chip"
          icon={<TriangleAlert aria-hidden />}
        >
          Needs attention
        </StatusPill>
      )}
      {draft.jurisdiction !== null && (
        <Mark icon={<Scale aria-hidden className={ICON} />}>
          {JURISDICTION_LABEL[draft.jurisdiction]}
        </Mark>
      )}
      {done && draft.latest_run?.finished_at != null && (
        <Mark icon={<Clock aria-hidden className={ICON} />}>
          Draft generated {dayOf(draft.latest_run.finished_at)}
        </Mark>
      )}
      {!done && draft.filing_deadline !== null && (
        <Mark icon={<Clock aria-hidden className={ICON} />}>
          Deadline {dayOfDate(draft.filing_deadline)}
        </Mark>
      )}
      {!done && stage !== undefined && (
        <Mark icon={<ListChecks aria-hidden className={ICON} />}>
          Next: {stage.label}
        </Mark>
      )}
    </div>
  );
}

/** The acts on a draft that are not opening it. Editing its details is for a draft still in progress. */
function DraftOptions({
  title,
  onEdit,
  onDelete,
}: {
  title: string;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <Menu name={`Options for ${title}`}>
      {onEdit !== undefined && (
        <MenuItem
          title="Edit draft details"
          description="Change the invention name, inventors, jurisdiction or filing deadline."
          onSelect={onEdit}
        />
      )}
      {onEdit !== undefined && onDelete !== undefined && <MenuSeparator />}
      {onDelete !== undefined && (
        <MenuItem
          title="Delete draft"
          description="Moves it to Recently deleted, from where it can be restored."
          onSelect={onDelete}
        />
      )}
    </Menu>
  );
}

/**
 * One draft on a client's page: amber while in progress, green once completed,
 * and the whole row opens the draft, at the stage it was last open at, through
 * the link on its title. Its Options sit above that link.
 */
export function DraftRow({
  draft,
  onEdit,
  onDelete,
}: {
  draft: TaskSummary;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const done = isCompleted(draft);
  // A draft being generated has its details fixed, so there is nothing to edit.
  const edit = done || isGenerating(draft) ? undefined : onEdit;
  const title = resolveDraftTitle(draft);
  const inventors = draft.inventors.map((inventor) => inventor.name);
  return (
    <LiftingItem
      testId={`draft-row-${draft.task_id}`}
      tone={done ? "success" : "warning"}
      className="text-foreground"
    >
      <div className="min-w-0 flex-1">
        <h3 className="text-[15px] font-bold tracking-[-0.01em]">
          <span className="mr-1.5 text-[11.5px] font-semibold text-subtle-foreground">
            Working title:
          </span>
          <MaybeLink
            to={draftOpeningPath(draft)}
            className="after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none"
          >
            {title}
          </MaybeLink>
        </h3>
        {inventors.length > 0 && (
          <p className="mt-[3px] text-[13.5px] font-semibold text-muted-foreground">
            <span className="mr-1.5 text-[11.5px] text-subtle-foreground">
              {inventors.length === 1 ? "Inventor:" : "Inventors:"}
            </span>
            {inventors.join(", ")}
          </p>
        )}
        <Marks draft={draft} done={done} />
      </div>
      {(edit !== undefined || onDelete !== undefined) && (
        <div className="relative z-10">
          <DraftOptions title={title} onEdit={edit} onDelete={onDelete} />
        </div>
      )}
      <GoesTo />
    </LiftingItem>
  );
}
